import cn from 'clsx'
import { StyledSvgIcon, setSvgIconSize } from 'LuxKit/SvgIcon'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { useSiteTakeoverTheme } from '../hooks/useSiteTakeoverTheme'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { useSiteTakeoverCampaignUIState } from '../hooks/useSiteTakeoverCampaignUIState'
import ModalContext from 'contexts/ModalContext'
import SiteTakeoverCampaignModal from './SiteTakeoverCampaignModal'
import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'

export const LABEL_HEIGHT = rem(24)

const IconWrapper = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const LabelElement = styled.span`
  box-sizing: border-box;
  height: ${LABEL_HEIGHT};
  max-width: fit-content;
  padding: 0 ${rem(8)};
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-wrap: nowrap;
  border: 1px transparent;
  border-radius: ${props => props.theme.borderRadius.S};
  gap: ${rem(4)};

  ${IconWrapper} ${StyledSvgIcon} {
    ${setSvgIconSize(16)}
  }

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(8)};
  }

  /* 
  TODO: Add more template variants here
  &.kind-primary {
    
  } */
`

const LabelText = styled(Caption)`
  font-size: ${rem(11)};
  font-weight: 500;
  line-height: ${rem(14)};

  ${mediaQueryUp.desktop} {
    font-size: ${rem(12)};
    line-height: ${rem(16)};
  }
`

type InheritedCaptionProps = Pick<React.ComponentProps<typeof Caption>, 'format'>
type Kind = 'custom'

interface Props extends React.HTMLAttributes<HTMLSpanElement>, InheritedCaptionProps {
  campaignTagId: string
  // if true, the campaign tag will be visible even if the feature flag is disabled
  // it should only be used when you want to reuse the component in other places
  forcedVisible?: boolean
  // when true, the label hides itself (unless forcedVisible) if the USP for the same campaign is visible
  suppressWhenUSPVisible?: boolean
  forcedShowIcon?: boolean
}

/**
 * This is a base 'abstract' label that is used by all the *implementation labels*
 * This should never be used directly. Talk to your designer to discover the correct
 * label type to use in the event you are unsure.
 */
export const SiteTakeoverCampaignTagLabel = React.forwardRef<HTMLSpanElement, Props>((props, ref) => {
  const {
    className,
    format,
    campaignTagId,
    forcedVisible,
    suppressWhenUSPVisible = false,
    ...rest
  } = props

  // fetch the theme from sitetakeover
  const siteTakeoverTheme = useSiteTakeoverTheme()
  const showModal = useContext(ModalContext)
  const {
    shouldShowTag,
    tagPayload,
    shouldShowModal,
  } = useSiteTakeoverCampaignUIState({
    campaignTagId,
    forceTagVisible: forcedVisible,
    suppressTagWhenUSPVisible: suppressWhenUSPVisible,
  })

  const shouldShowIcon = useLabFeatureFlag(LabFeatureFlags.enableTakeoverModal)?.enabled

  const handleModalOpen = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (!shouldShowModal) {
      return
    }
    showModal(<SiteTakeoverCampaignModal />)
  }, [shouldShowModal, showModal])

  if (!shouldShowTag || !tagPayload?.campaignTag) {
    return null
  }

  // TODO: Add more kind variants here from the CX templates
  const kind: Kind = 'custom'
  const paletteType = siteTakeoverTheme?.labelPalette ?? siteTakeoverTheme?.palette

  return <Pane paletteType={paletteType}>
    <LabelElement
      {...rest}
      ref={ref}
      className={cn(
        className,
        `kind-${kind}`,
      )}
      style={{
        background: siteTakeoverTheme?.labelStyle,
      }}
    >
      <LabelText variant="medium" format={format}>{tagPayload.campaignTag}</LabelText>
      {shouldShowIcon && <IconWrapper onClick={handleModalOpen}><LineInfoCircleIcon /></IconWrapper>}
    </LabelElement>
  </Pane>
})
